import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Fade, Card, CardContent, Typography } from '@mui/material';
import { Add, CloudUpload } from '@mui/icons-material';

import { useMannequinContext, HangerCard as IHangerCard } from '../../context/MannequinContext';
import { SCUIButton } from '../../../../atomic/atoms';
import { useHangerGeneration } from './hooks/useHangerGeneration';
import { HangerHeader } from './components/HangerHeader';
import { HangerCard } from './components/HangerCard/HangerCard';
import { SCUIImagePreviewModal } from '../../../../atomic/atoms';
import { AIGenerationTour, useAIGenerationTour } from './components/AIGenerationTour';
import AIGeneratorCommandService from '../../../../services/studio/StudioCommand/AIGenerator/AIGeneratorCommand.services';
import AIProcessLogQuery from '../../../../services/query/AIProcessLogQuery';
import {
    normalizeHangerCardsModelPolicy,
    hasHangerCardModelPolicyChanges,
} from './AIGeneration.helper';
import {
    ensureCardGenerationNotificationPreferenceLoaded,
    isCardGenerationNotificationsEnabled,
    toggleCardGenerationNotificationsEnabled,
} from './cardGenerationNotification';
import './styles/HangerPhotoUploadGenerate.scss';

const MAX_CARDS = 16;
const AUTO_SCROLL_ZONE = 120; // px from edge
const AUTO_SCROLL_MAX_SPEED = 25; // px per frame

const normalizeExtension = (ext: string): string => (ext.toLowerCase() === 'jpeg' ? 'jpg' : ext.toLowerCase());

const resolveDownloadExtension = (url: string, generatedOutputFormat?: string): string => {
    if (url.startsWith('data:')) {
        const mimeMatch = /^data:image\/(jpeg|jpg|png|webp|gif)/i.exec(url);
        return mimeMatch
            ? normalizeExtension(mimeMatch[1])
            : generatedOutputFormat
              ? normalizeExtension(generatedOutputFormat)
              : 'jpg';
    }
    const urlExtMatch = /\.(jpg|jpeg|png|webp|gif)(?:\?|$)/i.exec(url);
    if (urlExtMatch) {
        return normalizeExtension(urlExtMatch[1]);
    }
    return generatedOutputFormat ? normalizeExtension(generatedOutputFormat) : 'jpg';
};

const resolveGeneratedOutputFormat = (
    card: { generatedResults: Array<{ generatedUrls: string[]; outputFormat?: string }> } | undefined,
    url: string
): string | undefined => {
    if (!card) {
        return undefined;
    }

    const matchedResult = card.generatedResults.find((result) => result.generatedUrls.includes(url));
    return matchedResult?.outputFormat ?? card.generatedResults[0]?.outputFormat;
};

interface HangerPhotoUploadGenerateProps {
    onFinish: () => void;
    onBack: () => void;
    generationTypeName?: string | null;
}

export const HangerPhotoUploadGenerate: FC<HangerPhotoUploadGenerateProps> = ({ generationTypeName }) => {
    const { t } = useTranslation();
    const {
        hangerCards,
        addHangerCard,
        duplicateHangerCard,
        duplicateHangerCardWithPhotos,
        removeHangerCard,
        setHangerCards,
        createCardFromHistory,
        updateHangerCard,
        applyBackgroundOrLabelToCard,
        isDraftLoading,
    } = useMannequinContext();
    // const [error, setError] = useState<string>(''); // Removed global error state
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [downloadingCardId, setDownloadingCardId] = useState<string | null>(null);
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);

    // Layout State
    const [layoutMode, setLayoutMode] = useState<'single' | 'double' | 'triple' | 'quad' | 'quint'>(
        () => (localStorage.getItem('hangerLayoutMode') as any) || 'double'
    );

    // Custom Hooks
    const { handleGenerateCard, handleInpaint, loadingGifMap, inpaintingCardId, resumeGenerationsAfterReload } = useHangerGeneration();
    const resumeGenerationsCalledRef = useRef(false);

    useEffect(() => {
        if (isDraftLoading || resumeGenerationsCalledRef.current) {
            return;
        }
        resumeGenerationsCalledRef.current = true;
        void resumeGenerationsAfterReload();
    }, [isDraftLoading, resumeGenerationsAfterReload]);

    useEffect(() => {
        if (isDraftLoading) {
            return;
        }

        setHangerCards((prevCards) => {
            const normalizedCards = normalizeHangerCardsModelPolicy(prevCards);
            return hasHangerCardModelPolicyChanges(prevCards, normalizedCards) ? normalizedCards : prevCards;
        });
    }, [isDraftLoading, setHangerCards]);

    // -- Drag & Drop State (Cards) --
    const [draggingCardId, setDraggingCardId] = useState<string | null>(null);
    const [dragOverCardId, setDragOverCardId] = useState<string | null>(null);
    const [dragPosition, setDragPosition] = useState<'before' | 'after' | null>(null);
    // Refs mirror the state so that memoized callbacks always read the latest values
    const draggingCardIdRef = useRef<string | null>(null);
    const dragPositionRef = useRef<'before' | 'after' | null>(null);

    useEffect(() => {
        void ensureCardGenerationNotificationPreferenceLoaded().then(() => {
            setNotificationsEnabled(isCardGenerationNotificationsEnabled());
        });
    }, []);

    useEffect(() => {
        localStorage.setItem('hangerLayoutMode', layoutMode);
    }, [layoutMode]);

    // Üretim geçmişinden "tekrar üret" ile gelen veriyi oku
    useEffect(() => {
        const pendingId = localStorage.getItem('pendingHistoryItemId');
        if (pendingId) {
            localStorage.removeItem('pendingHistoryItemId');
            // Also clean up legacy key if exists
            localStorage.removeItem('pendingHistoryItem');

            const id = Number(pendingId);
            if (!isNaN(id)) {
                AIProcessLogQuery.GetHistoryDetail(id)
                    .then((historyItem) => {
                        createCardFromHistory(historyItem);
                    })
                    .catch((e) => {
                        console.error('Failed to fetch history detail for regeneration:', e);
                    });
            }
        }
    }, []);

    // -- Auto Scroll (document-level, page scrolls naturally) --
    const rootRef = useRef<HTMLDivElement>(null);
    const scrollSpeedRef = useRef<number>(0);
    const animationFrameRef = useRef<number | null>(null);
    const isDraggingRef = useRef<boolean>(false);
    const scrollableParentRef = useRef<HTMLElement | null>(null);

    // Find the nearest scrollable ancestor once
    const getScrollableParent = useCallback((): HTMLElement => {
        if (scrollableParentRef.current) return scrollableParentRef.current;

        let el = rootRef.current?.parentElement;
        while (el) {
            const style = getComputedStyle(el);
            const overflowY = style.overflowY;
            if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
                scrollableParentRef.current = el;
                return el;
            }
            el = el.parentElement;
        }
        // Fallback: documentElement (browser scroll)
        scrollableParentRef.current = document.documentElement;
        return document.documentElement;
    }, []);

    const stopAutoScroll = useCallback(() => {
        scrollSpeedRef.current = 0;
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    }, []);

    const performScroll = useCallback(() => {
        if (scrollSpeedRef.current === 0) {
            animationFrameRef.current = null;
            return;
        }
        const scrollEl = getScrollableParent();
        scrollEl.scrollTop += scrollSpeedRef.current;
        animationFrameRef.current = requestAnimationFrame(performScroll);
    }, [getScrollableParent]);

    // Global dragover listener - fires regardless of React event propagation
    useEffect(() => {
        const handleGlobalDragOver = (e: DragEvent) => {
            if (!isDraggingRef.current) return;

            const clientY = e.clientY;
            const viewportHeight = globalThis.innerHeight;

            if (clientY < AUTO_SCROLL_ZONE) {
                // Near top of viewport - scroll up
                const intensity = (AUTO_SCROLL_ZONE - clientY) / AUTO_SCROLL_ZONE;
                scrollSpeedRef.current = -(AUTO_SCROLL_MAX_SPEED * intensity);
            } else if (clientY > viewportHeight - AUTO_SCROLL_ZONE) {
                // Near bottom of viewport - scroll down
                const intensity = (clientY - (viewportHeight - AUTO_SCROLL_ZONE)) / AUTO_SCROLL_ZONE;
                scrollSpeedRef.current = AUTO_SCROLL_MAX_SPEED * intensity;
            } else {
                scrollSpeedRef.current = 0;
            }

            // Start animation loop if not already running
            if (scrollSpeedRef.current !== 0 && !animationFrameRef.current) {
                animationFrameRef.current = requestAnimationFrame(performScroll);
            }
        };

        const handleGlobalDragEnd = () => {
            isDraggingRef.current = false;
            stopAutoScroll();
        };

        document.addEventListener('dragover', handleGlobalDragOver);
        document.addEventListener('dragend', handleGlobalDragEnd);
        document.addEventListener('drop', handleGlobalDragEnd);

        return () => {
            document.removeEventListener('dragover', handleGlobalDragOver);
            document.removeEventListener('dragend', handleGlobalDragEnd);
            document.removeEventListener('drop', handleGlobalDragEnd);
            stopAutoScroll();
        };
    }, [performScroll, stopAutoScroll]);

    const handleCardDragStart = useCallback((e: React.DragEvent, cardId: string) => {
        const tagName = (e.target as HTMLElement).tagName;
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName)) {
            e.preventDefault();
            return;
        }
        setDraggingCardId(cardId);
        draggingCardIdRef.current = cardId;
        isDraggingRef.current = true;
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleCardDragOver = useCallback((e: React.DragEvent, targetCardId: string) => {
        e.preventDefault();
        const currentDragging = draggingCardIdRef.current;
        if (!currentDragging || currentDragging === targetCardId) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const pos = x > rect.width / 2 ? 'after' : 'before';
        setDragOverCardId(targetCardId);
        setDragPosition(pos);
        dragPositionRef.current = pos;
    }, []);

    const handleCardDrop = useCallback(
        (e: React.DragEvent, targetCardId: string) => {
            e.preventDefault();
            stopAutoScroll();
            const currentDragging = draggingCardIdRef.current;
            const currentPosition = dragPositionRef.current;
            if (!currentDragging || currentDragging === targetCardId) {
                setDraggingCardId(null);
                draggingCardIdRef.current = null;
                return;
            }

            setHangerCards((prevCards: IHangerCard[]) => {
                const sourceIndex = prevCards.findIndex((c) => c.id === currentDragging);
                const targetIndex = prevCards.findIndex((c) => c.id === targetCardId);
                if (sourceIndex === -1 || targetIndex === -1) return prevCards;

                const newCards = [...prevCards];
                const [moved] = newCards.splice(sourceIndex, 1);
                const adjustedIndex = newCards.findIndex((c) => c.id === targetCardId);
                newCards.splice(currentPosition === 'after' ? adjustedIndex + 1 : adjustedIndex, 0, moved);
                return newCards;
            });
            setDraggingCardId(null);
            setDragOverCardId(null);
            draggingCardIdRef.current = null;
            dragPositionRef.current = null;
        },
        [setHangerCards, stopAutoScroll]
    );

    const handleDownload = useCallback(
        async (cardId: string, url: string) => {
            setDownloadingCardId(cardId);
            try {
                const card = hangerCards.find((c) => c.id === cardId);
                const rawBarcode = card?.config?.barcode?.trim() || 'generated';
                const barcode = rawBarcode.split('+')[0].trim() || 'generated';

                const isDataUrl = url.startsWith('data:');
                const extension = resolveDownloadExtension(url, resolveGeneratedOutputFormat(card, url));
                const filename = `${barcode}.${extension}`;

                if (isDataUrl) {
                    // Base64 data URL → doğrudan blob'a çevirip indir
                    const res = await fetch(url);
                    const blob = await res.blob();
                    const blobUrl = globalThis.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    globalThis.URL.revokeObjectURL(blobUrl);
                } else if (card?.selectedModel === 'EachLabs') {
                    // EachLabs çıktıları public URL → direkt fetch ile indir
                    const response = await fetch(url);
                    const blob = await response.blob();
                    const blobUrl = globalThis.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    globalThis.URL.revokeObjectURL(blobUrl);
                } else {
                    // HTTP URL → BFF proxy üzerinden indir
                    await AIGeneratorCommandService.DownloadImage(url, filename, card?.predictionId ?? undefined);
                }

                // Tracking için backend MarkAsDownloaded'a her koşulda async olarak bilgi ver
                // if (card?.predictionId) {
                //     AIGeneratorCommandService.MarkAsDownloaded(card.predictionId).catch(console.error);
                // }
            } catch (e) {
                console.error(e);
                globalThis.open(url, '_blank');
            } finally {
                setDownloadingCardId(null);
            }
        },
        [hangerCards]
    );

    const handleCardDragEnd = useCallback(() => {
        setDraggingCardId(null);
        setDragOverCardId(null);
        draggingCardIdRef.current = null;
        dragPositionRef.current = null;
        isDraggingRef.current = false;
        stopAutoScroll();
    }, [stopAutoScroll]);

    const handleAddCard = useCallback(() => {
        if (hangerCards.length >= MAX_CARDS) {
            return;
        }
        addHangerCard();
    }, [hangerCards.length, addHangerCard]);

    const handleToggleNotifications = useCallback(() => {
        void toggleCardGenerationNotificationsEnabled().then((enabled) => {
            setNotificationsEnabled(enabled);
        });
    }, []);

    const { runTour, startTour, steps, locale, onCallback } = useAIGenerationTour({
        cardCount: hangerCards.length,
        ensureCardExists: addHangerCard,
        scrollContainerRef: rootRef,
    });

    return (
        <Box ref={rootRef} className="hanger-photo-upload-generate">
            <AIGenerationTour steps={steps} run={runTour} locale={locale} onCallback={onCallback} />
            <HangerHeader
                cardCount={hangerCards.length}
                maxCards={MAX_CARDS}
                layoutMode={layoutMode}
                onLayoutChange={setLayoutMode}
                onAddCard={handleAddCard}
                onStartTour={startTour}
                notificationsEnabled={notificationsEnabled}
                onToggleNotifications={handleToggleNotifications}
                generationTypeName={generationTypeName}
            />

            {/* We no longer show a global error alert here since each card shows its own error */}

            <Box className="hanger-photo-upload-generate__cards-container">
                {hangerCards.length === 0 ? (
                    <Fade in={true} timeout={600}>
                        <Card className="empty-state-card" elevation={2}>
                            <CardContent>
                                <CloudUpload className="empty-icon" />
                                <Typography variant="h6" gutterBottom>
                                    {t('mannequin.aiGeneration.hangerFlow.uploadGenerate.addCard')}
                                </Typography>
                                <SCUIButton
                                    type="primary"
                                    size="l"
                                    sizeType="default"
                                    text={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.addCard')}
                                    startIcon={<Add />}
                                    onClick={handleAddCard}
                                />
                            </CardContent>
                        </Card>
                    </Fade>
                ) : (
                    <Box
                        className={`hanger-photo-upload-generate__cards-scroll hanger-photo-upload-generate__cards-scroll--${layoutMode}`}
                    >
                        {hangerCards.map((card, index) => (
                            <HangerCard
                                key={card.id}
                                card={card}
                                index={index}
                                isTourTarget={index === 0}
                                tourActive={runTour}
                                isDragging={draggingCardId === card.id}
                                dragOverId={dragOverCardId}
                                dragPosition={dragPosition}
                                loadingGif={loadingGifMap[card.id]}
                                updateHangerCard={updateHangerCard}
                                applyBackgroundOrLabelToCard={applyBackgroundOrLabelToCard}
                                onDragStart={(e) => handleCardDragStart(e, card.id)}
                                onDragOver={(e) => handleCardDragOver(e, card.id)}
                                onDragEnd={handleCardDragEnd}
                                onDrop={(e) => handleCardDrop(e, card.id)}
                                onGenerate={handleGenerateCard}
                                onDuplicate={duplicateHangerCard}
                                onDuplicateWithPhotos={duplicateHangerCardWithPhotos}
                                onRemove={removeHangerCard}
                                onPreviewImage={setPreviewImage}
                                onDownload={handleDownload}
                                downloadingCardId={downloadingCardId}
                                // onError={setError}
                                onInpaint={handleInpaint}
                                inpaintingCardId={inpaintingCardId}
                                hangerCards={hangerCards}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            <SCUIImagePreviewModal imageUrl={previewImage} onClose={() => setPreviewImage(null)} />
        </Box>
    );
};
